import { useEffect, useMemo, useState, useCallback } from "react";
import { getServices } from "../services/servicesApi";

const BROKEN_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='800' height='500'>
      <rect width='100%' height='100%' fill='#eee'/>
      <text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle'
            fill='#888' font-family='sans-serif' font-size='18'>Image not found</text>
    </svg>`
  );

const ServicesSection = () => {
  const [activeCategory, setActiveCategory] = useState("tiling"); // 'tiling' or 'painting'
  const [tiling, setTiling] = useState(null);
  const [painting, setPainting] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // 🔄 fetch function (inverted boolean logic)
  const fetchCategory = useCallback(async (cat) => {
    setLoading(true);
    setErr("");
    try {
      // ⚠️ Your backend: tiling=false, painting=true
      const isPainting = cat === "painting";
      const data = await getServices({ tiling: isPainting }); // invert logic
      if (cat === "tiling") setTiling(data);
      else setPainting(data);
    } catch (e) {
      console.error("Services fetch error:", e);
      setErr("Failed to load services.");
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔹 Load current tab data on mount / switch
  useEffect(() => {
    if (activeCategory === "tiling" && tiling == null) fetchCategory("tiling");
    if (activeCategory === "painting" && painting == null)
      fetchCategory("painting");
  }, [activeCategory, tiling, painting, fetchCategory]);

  const list = useMemo(
    () => (activeCategory === "tiling" ? tiling : painting),
    [activeCategory, tiling, painting]
  );

  return (
    <section id="services" style={{ marginTop: "2rem" }}>
      <h2
        style={{ fontSize: "2rem", fontWeight: "800", marginBottom: "1.5rem" }}
      >
        Our Services
      </h2>

      {/* Filter Buttons */}
      <div style={{ display: "flex", gap: "12px", marginBottom: "1rem" }}>
        <button
          className={activeCategory === "tiling" ? "active" : ""}
          onClick={() => setActiveCategory("tiling")}
          style={tabStyle(activeCategory === "tiling")}
        >
          Tiling
        </button>
        <button
          className={activeCategory === "painting" ? "active" : ""}
          onClick={() => setActiveCategory("painting")}
          style={tabStyle(activeCategory === "painting")}
        >
          Painting
        </button>
      </div>

      {/* State */}
      {loading && <p>Loading...</p>}
      {!loading && err && <p style={{ color: "red" }}>{err}</p>}

      {/* Services Grid */}
      {!loading && !err && Array.isArray(list) && (
        <div style={gridStyle}>
          {list.map((svc) => (
            <div key={svc.id} style={cardStyle}>
              <div
                style={{
                  height: 180,
                  overflow: "hidden",
                  borderTopLeftRadius: 10,
                  borderTopRightRadius: 10,
                }}
              >
                <img
                  src={svc.cover_image || BROKEN_IMG}
                  alt={svc.title || "Service"}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: svc.cover_image ? "cover" : "contain",
                  }}
                  onError={(e) => {
                    e.currentTarget.src = BROKEN_IMG;
                    e.currentTarget.style.objectFit = "contain";
                  }}
                />
              </div>
              <div style={{ padding: "14px 16px" }}>
                <h3
                  style={{
                    margin: "0 0 6px",
                    fontWeight: 700,
                    fontSize: "1.1rem",
                  }}
                >
                  {svc.title || "Untitled Service"}
                </h3>
                <p style={{ margin: 0, color: "#555" }}>
                  {svc.subtitle || "Quality service at affordable price."}
                </p>
              </div>
            </div>
          ))}
          {list.length === 0 && (
            <p style={{ gridColumn: "1 / -1", color: "#666" }}>
              No services found for this category.
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default ServicesSection;

// 💅 Simple inline styles for quick styling
const tabStyle = (active) => ({
  padding: "10px 20px",
  borderRadius: 25,
  border: active ? "2px solid #111827" : "2px solid #cfd6dd",
  background: active ? "#111827" : "#f3f4f6",
  color: active ? "#fff" : "#111827",
  fontWeight: 700,
  cursor: "pointer",
});

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
  gap: 20,
};

const cardStyle = {
  borderRadius: 10,
  background: "#fff",
  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  overflow: "hidden",
  border: "1px solid #e5e7eb",
};
